import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Car, MapPin, Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState } from 'react';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LiftCapacityWarning } from '@/components/safety/LiftCapacityWarning';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { notifyPickupChanged } from '@/utils/pickupConvergence';
import {
  markCompletedTicketPickedUp,
  isPickupSettled,
  didPickUpNow,
  needsPickupReload,
  describePickupRefusal,
} from '@/utils/completedPickupLifecycle';
import { usePermissions } from '@/hooks/usePermissions';
import { CAPACITY_NOT_VERIFIED_TITLE, capacityNotVerifiedDescription, isCapacityNotVerified } from '@/lib/location/liftCapacityNotice';

interface ParkingSpot {
  id: string;
  label: string;
  spot_type: string;
  is_active: boolean;
}

interface ParkingSpotDetailSheetProps {
  spot: ParkingSpot | null;
  ticket: any | null;
  bays: any[];
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  readOnly?: boolean;
}

export const ParkingSpotDetailSheet = ({ 
  spot, 
  ticket, 
  bays,
  isOpen, 
  onClose,
  onRefresh,
  readOnly
}: ParkingSpotDetailSheetProps) => {
  const { toast } = useToast();
  const [isMovingToBay, setIsMovingToBay] = useState(false);
  const [isMarkingPickedUp, setIsMarkingPickedUp] = useState(false);
  // 8G.2C.7: the pickup capability, with the other hooks. Releasing a vehicle
  // to a customer is its own permission, not `canAssignTickets` borrowed
  // because the role sets happen to coincide today.
  const { canReleaseCompletedVehicle } = usePermissions();
  const [selectedBayId, setSelectedBayId] = useState<string>('');
  const [liftWarningData, setLiftWarningData] = useState<{
    gvwr: number;
    capacity: number;
    compatibleLifts: Array<{ bayId: string; bayName: string; liftCapacityLbs: number; bayType?: string }>;
    ticketId: string;
  } | null>(null);

  if (!spot) return null;

  const handleMoveToBay = async () => {
    if (!ticket || !selectedBayId) return;

    setIsMovingToBay(true);
    try {
      const { data: rawData, error } = await supabase.rpc('move_vehicle_location', {
        p_ticket_id: ticket.id,
        p_target_bay_id: selectedBayId,
      });

      if (error) throw error;

      const result = rawData as unknown as { success: boolean; reason?: string; liftSafetyBlock?: boolean; liftSafetyUnverified?: boolean; gvwr?: number; capacity?: number; capacityNotVerified?: boolean; compatibleLifts?: Array<{ bayId: string; bayName: string; liftCapacityLbs: number; bayType?: string }> };
      if (result && !result.success) {
        // The capacity dialog needs a KNOWN weight and capacity; a refusal for an unverified weight (OD-22) has
        // neither, so it is shown as the server's reason instead of a dialog with invented numbers.
        if (result.liftSafetyBlock && !result.liftSafetyUnverified && result.gvwr != null && result.capacity != null) {
          setLiftWarningData({
            gvwr: result.gvwr!,
            capacity: result.capacity!,
            compatibleLifts: result.compatibleLifts || [],
            ticketId: ticket.id,
          });
        } else {
          toast({
            title: 'Cannot move to bay',
            description: result.reason || 'Bay incompatible with ticket requirements',
            variant: 'destructive',
          });
        }
        // #15: a refusal means the view was behind — re-read the floor.
        onRefresh?.();
        return;
      }

      const targetBay = bays.find(b => b.id === selectedBayId);
      if (isCapacityNotVerified(result)) {
        // OD-33: allowed, never presented as safe.
        toast({ title: CAPACITY_NOT_VERIFIED_TITLE, description: capacityNotVerifiedDescription(targetBay?.name) });
      } else {
        toast({
          title: 'Moved to bay',
          description: `Vehicle moved to ${targetBay?.name || 'bay'}`,
        });
      }

      onRefresh?.();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to move vehicle',
        variant: 'destructive',
      });
    } finally {
      setIsMovingToBay(false);
    }
  };

  // 8G.2C.5: routed through the SERVER completed-pickup authority. This is the
  // SECOND production caller of the old unguarded pickup path — the independent
  // audit named only OptiboardTicketRow, and a repo-wide sweep for
  // `p_is_pickup` found this one too. Both are migrated; leaving either behind
  // would have kept the bypass reachable.
  const handleMarkPickedUp = async () => {
    if (!ticket || isMarkingPickedUp) return;

    setIsMarkingPickedUp(true);
    try {
      const pickup = await markCompletedTicketPickedUp(ticket.id);

      // Unknown result: reload rather than replay a human handover.
      if (needsPickupReload(pickup.outcome)) {
        toast({
          title: 'Pickup result unconfirmed',
          description: describePickupRefusal(pickup),
          variant: 'destructive',
        });
        onRefresh?.();
        return;
      }

      if (!isPickupSettled(pickup.outcome)) {
        toast({
          title: 'Cannot mark as picked up',
          description: describePickupRefusal(pickup),
          variant: 'destructive',
        });
        return;
      }

      // Gated on `didPickUpNow`, not on "settled": ALREADY_PICKED_UP is a
      // recovery for a car that left once, and must not produce a second human
      // pickup event. Client-derived and not exactly-once under a lost
      // response — consolidation is 8G.3.
      if (didPickUpNow(pickup.outcome) && pickup.shopId) {
        const { data: { user } } = await supabase.auth.getUser();
        logTicketEventToOcis({
          ticketId: ticket.id,
          shopId: pickup.shopId,
          eventType: 'ticket_status_changed',
          content: 'Vehicle picked up by customer',
          authorUserId: user?.id,
          payload: {
            reason: 'vehicle_picked_up',
            picked_up: true,
            picked_up_at: pickup.pickedUpAt,
            source: 'parking_spot_detail',
          },
        }).catch(err => console.error('[ParkingSpotDetailSheet] Pickup OCIS log error:', err));
      }

      // Every mounted surface, here and on the shop's other devices, re-reads
      // (FOH queue, Dayboard, Calendar). New pickups only.
      notifyPickupChanged({
        isNewPickup: didPickUpNow(pickup.outcome), ticketId: ticket.id,
        shopId: pickup.shopId, pickedUpAt: pickup.pickedUpAt, act: 'completed',
      });

      toast({
        title: didPickUpNow(pickup.outcome) ? 'Vehicle picked up' : 'Already picked up',
        description: didPickUpNow(pickup.outcome)
          ? undefined
          : 'This vehicle had already been handed back.',
      });
      onRefresh?.();
      onClose();
    } finally {
      setIsMarkingPickedUp(false);
    }
  };

  const activeBays = bays.filter(b => b.is_active);

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {spot.label}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Spot Status */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Status</span>
            <Badge variant={ticket ? 'default' : 'outline'}>
              {ticket ? 'Occupied' : 'Empty'}
            </Badge>
          </div>

          {ticket ? (
            <>
              <Separator />

              {/* Vehicle Info */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Car className="h-4 w-4" />
                  Vehicle
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Make/Model</span>
                    <span className="font-medium">
                      {ticket.vehicle?.year} {ticket.vehicle?.make} {ticket.vehicle?.model}
                    </span>
                  </div>
                  {ticket.vehicle?.license_plate && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Plate</span>
                      <span className="font-medium">{ticket.vehicle.license_plate}</span>
                    </div>
                  )}
                  {ticket.ro_number && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">RO Number</span>
                      <span className="font-medium">#{ticket.ro_number}</span>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Customer Info */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name</span>
                    <span className="font-medium">{ticket.customer_name || 'N/A'}</span>
                  </div>
                  {ticket.customer_phone && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone</span>
                      <span className="font-medium">{ticket.customer_phone}</span>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Scheduling Context */}
              <div className="space-y-3">
                <h3 className="font-semibold flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Scheduling
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <Badge variant={ticket.status === 'Completed' ? 'default' : 'secondary'}>
                      {ticket.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Technician</span>
                    <span className="font-medium">
                      {ticket.technician?.name || ticket.techName || 'Unassigned'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Assigned Bay</span>
                    <span className="font-medium">
                      {ticket.bay?.name || ticket.bayName || 'None'}
                    </span>
                  </div>
                  {ticket.scheduled_date && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Scheduled Date</span>
                      <span className="font-medium">{ticket.scheduled_date}</span>
                    </div>
                  )}
                  {ticket.scheduled_start_time && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Scheduled Time</span>
                      <span className="font-medium">
                        {format(new Date(ticket.scheduled_start_time), 'h:mm a')}
                      </span>
                    </div>
                  )}
                  {ticket.completed_at && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Completed</span>
                      <span className="font-medium">
                        {format(new Date(ticket.completed_at), 'MMM d, h:mm a')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <Separator />

              {/* Actions (hidden for readOnly/technician users) */}
              {!readOnly && (
              <div className="space-y-3">
                <h3 className="font-semibold">Actions</h3>

                {/* Move to Bay */}
                <div className="space-y-2">
                  <label className="text-sm text-muted-foreground">Move to Bay</label>
                  <div className="flex gap-2">
                    <Select value={selectedBayId} onValueChange={setSelectedBayId}>
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select bay..." />
                      </SelectTrigger>
                      <SelectContent>
                        {activeBays.map(bay => (
                          <SelectItem key={bay.id} value={bay.id}>
                            {bay.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleMoveToBay}
                      disabled={!selectedBayId || isMovingToBay}
                      size="sm"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Mark Picked Up — 8G.2C.7: gated on the pickup capability,
                    NOT on `readOnly`/canAssignTickets. Moving a car to a bay
                    and releasing one to a customer are different capabilities
                    whose role sets merely coincide today. */}
                {ticket.status === 'Completed' && canReleaseCompletedVehicle && (
                  <Button
                    onClick={handleMarkPickedUp}
                    variant="outline"
                    className="w-full"
                    disabled={isMarkingPickedUp}
                  >
                    {isMarkingPickedUp ? 'Marking...' : 'Mark as Picked Up'}
                  </Button>
                )}
              </div>
              )}
            </>
          ) : (
            <div className="py-8 text-center text-muted-foreground">
              <Car className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No vehicle currently parked in this spot</p>
            </div>
          )}
        </div>
      </SheetContent>
      {/* Lift Safety Warning Dialog */}
      <LiftCapacityWarning
        open={!!liftWarningData}
        onClose={() => setLiftWarningData(null)}
        gvwr={liftWarningData?.gvwr ?? 0}
        capacity={liftWarningData?.capacity ?? 0}
        compatibleLifts={liftWarningData?.compatibleLifts}
        onSelectLift={async (bayId) => {
          if (!liftWarningData) return;
          setLiftWarningData(null);
          setSelectedBayId(bayId);
          // Re-run handleMoveToBay logic with the selected bay
          setIsMovingToBay(true);
          try {
            const { data: rawData, error } = await supabase.rpc('move_vehicle_location', {
              p_ticket_id: liftWarningData.ticketId,
              p_target_bay_id: bayId,
            });
            if (error) throw error;
            const result = rawData as unknown as { success: boolean; reason?: string };
            if (result && !result.success) {
              toast({ title: 'Cannot move to bay', description: result.reason || 'Move failed', variant: 'destructive' });
              onRefresh?.();
              return;
            }
            const targetBay = bays.find(b => b.id === bayId);
            toast({ title: 'Moved to bay', description: `Vehicle moved to ${targetBay?.name || 'bay'}` });
            onRefresh?.();
            onClose();
          } catch (err: any) {
            toast({ title: 'Error', description: err.message || 'Failed to move vehicle', variant: 'destructive' });
          } finally {
            setIsMovingToBay(false);
          }
        }}
      />
    </Sheet>
  );
};
